import {
  FilterOptionKeys,
  Goal,
  GoalFilterDocument,
  GoalImpactLevel,
  ID,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { goalNoImpactFilterValue } from '../constants/goal-list-page-options';
import { getSubGoals } from './get-sub-goals';
import { matchesDateFilter } from './matches-date-filter';

export type FilterGoalsByFilterDocumentOptions = {
  today: Timestamp;
  weekStartsOn: WeekDays;
};

const filterByLifeArea = (goals: Goal[], lifeAreaIds: ID[]): Goal[] => {
  if (!lifeAreaIds.length) {
    return [];
  }

  const validIds = new Set<ID>();
  const rootGoals = goals.filter(
    (goal) => goal.lifeAreaId && lifeAreaIds.includes(goal.lifeAreaId),
  );

  rootGoals.forEach((rootGoal) => {
    validIds.add(rootGoal.id);
    getSubGoals(rootGoal.id, { goals, recursive: true }).forEach((subGoal) =>
      validIds.add(subGoal.id),
    );
  });

  return goals.filter((goal) => validIds.has(goal.id));
};

const filterByGoal = (goals: Goal[], goalIds: ID[]): Goal[] => {
  if (!goalIds.length) {
    return [];
  }

  return goals.filter(
    (goal) =>
      goalIds.includes(goal.id) ||
      !!goal.parentIds?.some((parentId) => goalIds.includes(parentId)),
  );
};

const filterByImpact = (goals: Goal[], include: string[]): Goal[] => {
  if (!include.length) {
    return goals;
  }

  const includeNoImpact = include.includes(goalNoImpactFilterValue);

  return goals.filter((goal) => {
    if (!goal.impact) {
      return includeNoImpact;
    }
    return include.includes(goal.impact as GoalImpactLevel);
  });
};

export const filterGoalsByFilterDocument = (
  goals: Goal[],
  filterDocument: GoalFilterDocument | undefined,
  { today, weekStartsOn }: FilterGoalsByFilterDocumentOptions,
): Goal[] => {
  const filters = filterDocument?.filters;
  if (!filters) {
    return goals;
  }

  let result = goals;

  const lifeAreaInclude = filters[FilterOptionKeys.LifeArea]?.include;
  if (lifeAreaInclude) {
    result = filterByLifeArea(result, lifeAreaInclude);
  }

  const goalInclude = filters[FilterOptionKeys.Goal]?.include;
  if (goalInclude) {
    result = filterByGoal(result, goalInclude);
  }

  const startDateFilter = filters[FilterOptionKeys.Date];
  if (startDateFilter?.type) {
    result = result.filter((goal) =>
      matchesDateFilter(goal.startDate, startDateFilter, {
        today,
        weekStartsOn,
      }),
    );
  }

  const deadlineFilter = filters[FilterOptionKeys.Deadline];
  if (deadlineFilter?.type) {
    result = result.filter((goal) =>
      matchesDateFilter(goal.deadline, deadlineFilter, { today, weekStartsOn }),
    );
  }

  const impactInclude = filters[FilterOptionKeys.GoalImpact]?.include;
  if (impactInclude) {
    result = filterByImpact(result, impactInclude);
  }

  return result;
};
